/**
 * 跳躍グラフ（CLAUDE.md §3.2 — このゲームの心臓部）
 *
 * 辺の条件はプロトタイプで検証済みのものをそのまま採用している。
 * 上りと下りが非対称であることがパズル性の全てなので、**有向グラフ**として扱う。
 * 無向化してはいけない。
 *
 * 歩き:  dr === 0 && |dc| === 1
 * 上り:  1 <= -dr <= maxUp && |dc| <= maxSide
 *        かつ 出発マスの真上（自分の列）が目標の高さまで空いている
 *        かつ 目標の高さで、水平に渡る間が空いている
 * 落下:  0 < dr <= maxFall && |dc| <= 1
 *        かつ 落下する列が着地点まで空いている
 *
 * よじ登り／よじ降り（キャットタワーだけの例外）:
 *   |dr| === 1 && |dc| <= 1 && 出発か目標のどちらかの足元がキャットタワーのマス
 *   L字の抜け道や落下の縦の空きを要求しない。
 *   キャットタワーは猫が登り降りするために作られた物なので、段の張り出しの下からでも
 *   爪を立てて上がれるし、段づたいに降りられる。
 *   廃墟の地形は一切変えないので §3.2 の非対称は保たれる（箱の柱は登りにくいまま）。
 */
import { Grid, isClimbable, isNode, isPlayerPlaced, isSolid, keyOf } from './grid';

export type NodeKey = number;
export type EdgeKind = 'walk' | 'up' | 'fall' | 'climb';

export interface Edge {
  to: NodeKey;
  kind: EdgeKind;
}

/**
 * 猫の種類ごとに変わる（§3.2 末尾／§3.3）。
 * ここに入るのは「グラフの形が変わる」ものだけ。
 * 「行きたがらない」（子猫の高所嫌い、臆病な猫の混雑嫌い）は
 * グラフではなく経路探索のコスト側に置く。
 */
export interface JumpParams {
  maxUp: number;
  maxSide: number;
  maxFall: number;
  /** 細い足場（孤立した箱の上）に乗れない。太った猫（§3.3） */
  noNarrow?: boolean;
}

export interface JumpGraph {
  nodes: Map<NodeKey, { c: number; r: number }>;
  edges: Map<NodeKey, Edge[]>;
  params: JumpParams;
}

function clearColumn(grid: Grid, c: number, rTop: number, rBot: number): boolean {
  for (let r = rTop; r <= rBot; r++) if (isSolid(grid, c, r)) return false;
  return true;
}

function clearRow(grid: Grid, r: number, cA: number, cB: number): boolean {
  const a = Math.min(cA, cB);
  const b = Math.max(cA, cB);
  for (let c = a; c <= b; c++) if (isSolid(grid, c, r)) return false;
  return true;
}

/** 孤立した1マスの足場か（両隣に支えが無い設置物の上） */
export function isNarrow(grid: Grid, c: number, r: number): boolean {
  if (!isPlayerPlaced(grid, c, r + 1)) return false;
  // キャットタワーの段は幅がある。太った猫も乗れる
  if (isClimbable(grid, c, r + 1)) return false;
  return !isSolid(grid, c - 1, r + 1) && !isSolid(grid, c + 1, r + 1);
}

/**
 * 全ノードを走査して有向グラフを組む。
 * プロトは全ノード総当たり O(N^2) だったが、辺の届く範囲は
 * dr ∈ [-maxUp, maxFall] / dc ∈ [-maxSide, maxSide] に限られるので
 * 近傍だけ見る形にしてある（§5.2）。辺の意味は総当たり版と同一。
 */
export function buildGraph(grid: Grid, params: JumpParams): JumpGraph {
  const nodes = new Map<NodeKey, { c: number; r: number }>();
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      if (!isNode(grid, c, r)) continue;
      if (params.noNarrow && isNarrow(grid, c, r)) continue;
      nodes.set(keyOf(grid, c, r), { c, r });
    }
  }

  const { maxUp, maxSide, maxFall } = params;
  const side = Math.max(maxSide, 1); // 落下は |dc| <= 1 を使うので最低1は見る
  const edges = new Map<NodeKey, Edge[]>();

  for (const [k, n] of nodes) {
    const out: Edge[] = [];
    for (let dr = -maxUp; dr <= maxFall; dr++) {
      for (let dc = -side; dc <= side; dc++) {
        if (dr === 0 && dc === 0) continue;
        const c = n.c + dc;
        const r = n.r + dr;
        const k2 = keyOf(grid, c, r);
        if (!nodes.has(k2)) continue;

        // どちらかの足元がキャットタワーなら、段づたいに登り降りできる
        const onTower = isClimbable(grid, n.c, n.r + 1) || isClimbable(grid, c, r + 1);

        if (dr === 0) {
          // 歩き
          if (Math.abs(dc) === 1) out.push({ to: k2, kind: 'walk' });
          continue;
        }

        if (dr < 0) {
          // よじ登り。キャットタワーの段には、張り出しの下からでも取り付ける
          if (dr === -1 && Math.abs(dc) <= 1 && onTower) {
            out.push({ to: k2, kind: 'climb' });
            continue;
          }
          // 上り
          if (-dr > maxUp || Math.abs(dc) > maxSide) continue;
          if (!clearColumn(grid, n.c, r, n.r - 1)) continue; // まず真上へ抜ける
          if (!clearRow(grid, r, n.c, c)) continue; // その高さで横へ渡る
          out.push({ to: k2, kind: 'up' });
        } else {
          // よじ降り。段づたいなら、真下が塞がっていても降りられる
          if (dr === 1 && Math.abs(dc) <= 1 && onTower) {
            out.push({ to: k2, kind: 'climb' });
            continue;
          }
          // 落下
          if (dr > maxFall || Math.abs(dc) > 1) continue;
          if (!clearColumn(grid, c, n.r, r - 1)) continue;
          if (Math.abs(dc) === 1 && isSolid(grid, c, n.r)) continue;
          out.push({ to: k2, kind: 'fall' });
        }
      }
    }
    edges.set(k, out);
  }

  return { nodes, edges, params };
}

/** 複数の出発点からの幅優先。戻り値は prev マップ（到達集合でもある）。 */
export function bfs(graph: JumpGraph, starts: readonly NodeKey[]): Map<NodeKey, NodeKey | null> {
  const prev = new Map<NodeKey, NodeKey | null>();
  const q: NodeKey[] = [];
  for (const s of starts) {
    if (graph.nodes.has(s) && !prev.has(s)) {
      prev.set(s, null);
      q.push(s);
    }
  }
  for (let i = 0; i < q.length; i++) {
    const cur = q[i];
    const es = graph.edges.get(cur);
    if (!es) continue;
    for (const e of es) {
      if (!prev.has(e.to)) {
        prev.set(e.to, cur);
        q.push(e.to);
      }
    }
  }
  return prev;
}

export function pathTo(prev: Map<NodeKey, NodeKey | null>, goal: NodeKey): NodeKey[] | null {
  if (!prev.has(goal)) return null;
  const path: NodeKey[] = [];
  let cur: NodeKey | null | undefined = goal;
  while (cur !== null && cur !== undefined) {
    path.unshift(cur);
    cur = prev.get(cur);
  }
  return path;
}

export function edgeKind(graph: JumpGraph, from: NodeKey, to: NodeKey): EdgeKind | null {
  const es = graph.edges.get(from);
  if (!es) return null;
  for (const e of es) if (e.to === to) return e.kind;
  return null;
}

export function paramsKey(p: JumpParams): string {
  return `${p.maxUp}/${p.maxSide}/${p.maxFall}/${p.noNarrow ? 'n' : '-'}`;
}

/**
 * 重み付きの経路探索（§3.3）。
 * 「通れる／通れない」はグラフが決めるが、「通りたい／通りたくない」はここで決める。
 * 子猫が高い所を嫌がる、臆病な猫が混んだ道を避ける、はこのコストで表す。
 *
 * 決定論を守るため、同じコストの時は必ずノード番号の小さい方を選ぶ。
 */
export interface SearchCost {
  /** そのマスに入る時の追加コスト */
  node?: (n: { c: number; r: number }, key: NodeKey) => number;
  /** 辺の種類ごとの追加コスト */
  edge?: (kind: EdgeKind) => number;
}

export interface SearchResult {
  prev: Map<NodeKey, NodeKey | null>;
  dist: Map<NodeKey, number>;
}

export function searchFrom(graph: JumpGraph, start: NodeKey, cost?: SearchCost): SearchResult {
  const prev = new Map<NodeKey, NodeKey | null>();
  const dist = new Map<NodeKey, number>();
  if (!graph.nodes.has(start)) return { prev, dist };

  dist.set(start, 0);
  prev.set(start, null);
  const open: NodeKey[] = [start];
  const done = new Set<NodeKey>();

  while (open.length) {
    // ノード数が小さいので線形探索で足りる。増えたらヒープに変える（§5.2）
    let bi = 0;
    for (let i = 1; i < open.length; i++) {
      const a = dist.get(open[i])!;
      const b = dist.get(open[bi])!;
      if (a < b || (a === b && open[i] < open[bi])) bi = i;
    }
    const cur = open.splice(bi, 1)[0];
    if (done.has(cur)) continue;
    done.add(cur);

    const base = dist.get(cur)!;
    for (const e of graph.edges.get(cur) ?? []) {
      if (done.has(e.to)) continue;
      const n = graph.nodes.get(e.to);
      if (!n) continue;
      const w = 1 + (cost?.edge?.(e.kind) ?? 0) + (cost?.node?.(n, e.to) ?? 0);
      const next = base + w;
      const known = dist.get(e.to);
      // 同じコストなら番号の小さい方を親にする（再現性のため）
      if (known === undefined || next < known || (next === known && cur < (prev.get(e.to) ?? Infinity))) {
        dist.set(e.to, next);
        prev.set(e.to, cur);
        if (known === undefined) open.push(e.to);
      }
    }
  }
  return { prev, dist };
}
